import { getNotificationUnreadCount, listNotifications } from "@/entities/notification/notificationsApi"
import type { NotificationDto } from "@/entities/notification/types"
import { ApiError } from "@/lib/api/http"
import { withAccessTokenRetry } from "@/lib/api/withAccessTokenRetry"
import type { TokenResponse } from "@/lib/api/http"

type SetSession = (tokens: TokenResponse) => void

type StartOptions = {
  getAccessToken: () => string | null
  setSession: SetSession
  /** Вызывается для каждого «нового» уведомления, подхваченного по дельте счётчика. */
  onNewNotifications: (items: NotificationDto[]) => void
  /** Синхронный апдейт счётчика — вызывается после каждого успешного /unread-count. */
  onUnreadCount: (count: number) => void
}

type Controller = {
  stop: () => void
  /** Принудительный опрос (например, после visibility → visible). */
  refresh: () => void
}

const BASE_INTERVAL_MS = 30_000
const ERROR_BACKOFF_MIN_MS = 60_000
const ERROR_BACKOFF_MAX_MS = 5 * 60_000

/**
 * Запасной режим опроса, когда SSE недоступен. Раз в 30 с читает /unread-count;
 * при росте счётчика — фетчит последнюю страницу и диффит по eventId, чтобы передать
 * только новые уведомления. Пауза при document.hidden, exponential backoff при ошибках.
 */
export const startPollingFallback = ({
  getAccessToken,
  setSession,
  onNewNotifications,
  onUnreadCount,
}: StartOptions): Controller => {
  let stopped = false
  let timer: ReturnType<typeof setTimeout> | null = null
  let lastUnread = 0
  let lastSeenEventId = 0
  let errorStreak = 0
  let inFlight: Promise<void> | null = null

  const clearTimer = () => {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
  }

  const computeDelay = () => {
    if (errorStreak === 0) return BASE_INTERVAL_MS
    const delay = ERROR_BACKOFF_MIN_MS * 2 ** (errorStreak - 1)
    return Math.min(delay, ERROR_BACKOFF_MAX_MS)
  }

  const schedule = () => {
    if (stopped) return
    clearTimer()
    timer = setTimeout(() => {
      void tick()
    }, computeDelay())
  }

  const tick = async () => {
    if (stopped) return
    if (typeof document !== "undefined" && document.hidden) {
      schedule()
      return
    }
    if (inFlight !== null) return
    inFlight = (async () => {
      const token = getAccessToken()
      if (!token) {
        schedule()
        return
      }
      try {
        const { count } = await withAccessTokenRetry(token, setSession, (t) => getNotificationUnreadCount(t))
        errorStreak = 0
        onUnreadCount(count)

        if (count > lastUnread) {
          /** Count вырос — фетчим последнюю страницу, фильтруем уже виденное. */
          const pageSize = Math.min(100, Math.max(20, count - lastUnread + 10))
          const page = await withAccessTokenRetry(token, setSession, (t) =>
            listNotifications(t, { maxResultCount: pageSize }),
          )
          const fresh = page.items.filter((item) => item.eventId > lastSeenEventId).sort((a, b) => a.eventId - b.eventId)
          if (fresh.length > 0) {
            lastSeenEventId = Math.max(lastSeenEventId, ...fresh.map((n) => n.eventId))
            onNewNotifications(fresh)
          } else if (page.items.length > 0) {
            lastSeenEventId = Math.max(lastSeenEventId, ...page.items.map((n) => n.eventId))
          }
        }
        lastUnread = count
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          /** withAccessTokenRetry уже попытался — если не помогло, просто ждём следующего цикла. */
        }
        errorStreak += 1
      } finally {
        inFlight = null
        schedule()
      }
    })()
    await inFlight
  }

  const handleVisibility = () => {
    if (stopped) return
    if (typeof document !== "undefined" && !document.hidden) {
      void tick()
    }
  }

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", handleVisibility)
  }

  /** Первый запрос — сразу, чтобы получить исходный счётчик и запомнить lastSeenEventId. */
  void tick()

  return {
    stop() {
      stopped = true
      clearTimer()
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibility)
      }
    },
    refresh() {
      if (stopped) return
      void tick()
    },
  }
}
